import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import Table from '../../../components/table/Table';
import { fetch } from '../../../utils/api-wrapper';
import InvoicePreview from '../../../components/Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import FormTextField from '../../../components/form-textfield';
import LoadingSpinner from '../../../components/loading-spinner/loading-spinner';
import Modal from '../../../components/modal.js';
import toast from 'react-hot-toast';
import Tooltip from '../../../components/tooltip/tooltip';

const columnNameKeys = {
  expense_preview: 'cc_statements.uncategorized_expenses.column_names.expense_preview',
  expense_date: 'cc_statements.uncategorized_expenses.column_names.date',
  possible_masks: 'cc_statements.uncategorized_expenses.column_names.possible_masks',
  total_amount: 'cc_statements.uncategorized_expenses.column_names.total_amount',
  user: 'cc_statements.uncategorized_expenses.column_names.user',
  explanation: 'cc_statements.uncategorized_expenses.column_names.explanation',
  restart_matching: 'cc_statements.uncategorized_expenses.column_names.restart_matching',
};

const UncategorizedExpensesTable = ({ data, refreshStatementData, filteredMask = '' }) => {
  const [uncategorizedExpenses, setUncategorizedExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldsAreUpdating, setFieldsAreUpdating] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteExpenseConfirmation, setShowDeleteExpenseConfirmation] = useState(false);

  const prepareTableData = (expenses) => {
    const tableData = expenses.map((expense) => ({
      // Provide a stable identifier so selection comparison can rely on _id
      _id: expense?._id,
      expense_preview: <InvoicePreview key={`${expense?.checksum}-${expense?.json?.Name}`} file_name={expense?.json?.Name || expense.file_name} currentInvoice={null} />,
      expense_date: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='date'
          value={expense?.json?.InvoiceDate || ''}
          validateChange={(val) => {
            updateExpense({ ...expense, json: { ...expense?.json, InvoiceDate: val } });
          }}
        />
      ),
      possible_masks: (expense?.user?.cards?.map((card) => `${card.card_masked_number}`) || []).join(', '),
      total_amount: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='money'
          value={expense?.json?.TotalWithTax ? `${expense?.json.TotalWithTax.toFixed(2)}` : '0.00'}
          validateChange={(val) => {
            const parsed = Number.parseFloat(val);
            if (!Number.isFinite(parsed)) return; // ignore invalid input
            updateExpense({ ...expense, json: { ...expense?.json, TotalWithTax: parsed } });
          }}
        />
      ),
      user: (() => {
        const first = expense?.user?.first_name?.trim();
        const last = expense?.user?.last_name?.trim();
        const combined = [first, last].filter(Boolean).join(' ').trim();
        return combined || 'N/A';
      })(),
      explanation: (
        <Tooltip message={expense?.reason || 'N/A'}>
          <div className='text-truncate'>{expense?.reason || 'N/A'}</div>
        </Tooltip>
      ),
      restart_matching: (
        <button
          className='btn btn-sm btn-primary w-100 d-flex justify-content-center align-items-center'
          disabled={expense?.__restarting}
          onClick={() => restartMatching(expense?.uncategorized_expense_id)}
        >
          {expense?.__restarting ? (
            <LoadingSpinner data-testid={`restart-spinner-${expense?._id}`} />
          ) : (
            <>
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_statements.uncategorized_expenses.column_names.restart_matching')}
            </>
          )}
        </button>
      ),
      raw_expense: expense,
    }));

    if (filteredMask && filteredMask !== 'all') {
      return tableData.filter((row) => row.possible_masks.includes(filteredMask));
    }

    return tableData;
  };

  useEffect(() => {
    fetchUncategorizedExpenses();
  }, []);

  const fetchUncategorizedExpenses = async () => {
    setLoading(true);
    fetch(
      '/expenses/uncategorized',
      'POST',
      {},
      (response) => {
        const uncategorizedExpenses = response?.data?.uncategorized_expenses || [];
        setUncategorizedExpenses(uncategorizedExpenses);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching uncategorized expenses:', error);
        setLoading(false);
      },
      true
    );
  };

  const restartMatching = async (expenseId) => {
    // Mark expense as restarting
    setUncategorizedExpenses((prev) => prev.map((e) => (e.uncategorized_expense_id === expenseId ? { ...e, __restarting: true } : e)));

    await fetch(
      `/expenses/uncategorized/${expenseId}`,
      'POST',
      {},
      () => {
        fetchUncategorizedExpenses();
        refreshStatementData();
      },
      (error) => {
        console.error('Error restarting matching for expense:', expenseId, error);
        setUncategorizedExpenses((prev) => prev.map((e) => (e.uncategorized_expense_id === expenseId ? { ...e, __restarting: false } : e)));
      },
      true
    );
  };

  const updateExpense = async (updatedExpense) => {
    setFieldsAreUpdating(true);

    // Optimistically update the UI
    const previousSnapshot = uncategorizedExpenses;
    setUncategorizedExpenses((prev) => prev.map((e) => (e._id === updatedExpense._id ? updatedExpense : e)));

    try {
      await fetch(
        '/expenses/uncategorized',
        'PUT',
        updatedExpense,
        () => {},
        (error) => {
          throw error;
        }
      );
    } catch {
      setUncategorizedExpenses(previousSnapshot);
    } finally {
      setFieldsAreUpdating(false);
    }
  };

  const lastSelectionRef = useRef([]);
  const onSelectedRowsChange = useCallback((rows) => {
    if (!Array.isArray(rows)) return;
    const prev = lastSelectionRef.current;
    const sameLength = prev.length === rows.length;
    const sameContent =
      sameLength &&
      prev.every((r, i) => {
        const prevId = r?._id ?? r;
        const nextId = rows[i]?._id ?? rows[i];
        return prevId === nextId;
      });
    if (sameContent) return; // no logical change
    lastSelectionRef.current = rows;
    setSelectedRows(rows);
  }, []);

  const deleteSelectedExpenses = () => {
    const expense_ids = selectedRows.map((exp) => exp.raw_expense.uncategorized_expense_id);
    fetch(
      '/expenses/uncategorized',
      'DELETE',
      { expense_ids },
      (response) => {
        toast.success(data.t.read('cc_transactions.delete_successful') || 'Expenses deleted successfully');
        fetchUncategorizedExpenses();
      },
      () => {
        toast.error(data.t.read('cc_transactions.delete_failed') || 'Failed to delete expenses');
      }
    );
    setShowDeleteExpenseConfirmation(false);
  };

  const memoizedTableData = useMemo(() => prepareTableData(uncategorizedExpenses), [uncategorizedExpenses, fieldsAreUpdating, filteredMask]);

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3 mt-5'>
        <h3 className='m-0'>{data.t.read('cc_statements.uncategorized_expenses.title')}</h3>
        {selectedRows && selectedRows.length > 0 && (
          <button className='btn btn-danger' onClick={() => setShowDeleteExpenseConfirmation(true)}>
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_transactions.delete_expenses')}
          </button>
        )}
      </div>
      <Table
        columnNames={columnNameKeys}
        tableData={memoizedTableData}
        searchable={false}
        isLoading={loading}
        selectableRows
        onRowSelect={onSelectedRowsChange}
      />
      {showDeleteExpenseConfirmation && (
        <Modal
          width='50vw'
          title={data.t.read('cc_transactions.delete_expenses')}
          cancelAction={() => setShowDeleteExpenseConfirmation(false)}
          okAction={deleteSelectedExpenses}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          <p>{`${data.t.read('cc_transactions.delete_expenses_confirm')} (${selectedRows.length})${data.t.read(
            'cc_transactions.delete_expenses_confirm2'
          )}`}</p>
        </Modal>
      )}
    </div>
  );
};

export default inject('data')(observer(UncategorizedExpensesTable));
